/**
 * Deterministic pseudo-randomness.
 *
 * The fixture world must be byte-identical on every load: a demo where the
 * numbers move between refreshes is impossible to design against, impossible to
 * screenshot, and impossible to assert on in a test. Everything random in this
 * app comes through here, seeded once.
 */

export interface Rng {
  /** Uniform in [0, 1). */
  next(): number
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number
  /** Uniform float in [min, max). */
  float(min: number, max: number): number
  /** True with probability p. */
  chance(p: number): boolean
  /** Uniform element. Throws on an empty array so a bad pool fails loudly. */
  pick<T>(items: readonly T[]): T
  /** n distinct elements, or all of them if n exceeds the pool. */
  sample<T>(items: readonly T[], n: number): T[]
  /** A new shuffled copy. */
  shuffle<T>(items: readonly T[]): T[]
  /** Roughly normal via the sum of three uniforms, clamped to [min, max]. */
  gauss(mean: number, stdDev: number, min?: number, max?: number): number
  /**
   * Long-tailed positive value — stakes, turnover and bet counts are all
   * log-normal in practice, and a uniform distribution makes every chart look
   * artificially flat.
   */
  logNormal(median: number, sigma: number): number
  /** A child generator, so one subsystem's draws cannot shift another's. */
  fork(salt: string): Rng
}

/** mulberry32 — small, fast, good enough for fixtures. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** FNV-1a, so a string salt maps to a stable seed. */
export function hashSeed(input: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h >>> 0
}

export function createRng(seed: number | string): Rng {
  const numeric = typeof seed === 'number' ? seed : hashSeed(seed)
  const next = mulberry32(numeric)

  const rng: Rng = {
    next,
    int: (min, max) => Math.floor(next() * (max - min + 1)) + min,
    float: (min, max) => next() * (max - min) + min,
    chance: (p) => next() < p,
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('rng.pick called with an empty array')
      return items[Math.floor(next() * items.length)] as T
    },
    sample<T>(items: readonly T[], n: number): T[] {
      return rng.shuffle(items).slice(0, Math.min(n, items.length))
    },
    shuffle<T>(items: readonly T[]): T[] {
      const out = items.slice()
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1))
        const a = out[i] as T
        out[i] = out[j] as T
        out[j] = a
      }
      return out
    },
    gauss(mean, stdDev, min = -Infinity, max = Infinity) {
      const u = (next() + next() + next()) / 3 // ~N(0.5, 1/36)
      const v = mean + (u - 0.5) * 6 * stdDev
      return Math.min(max, Math.max(min, v))
    },
    logNormal(median, sigma) {
      const u = Math.max(1e-9, next())
      const v = Math.max(1e-9, next())
      const normal = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
      return median * Math.exp(sigma * normal)
    },
    fork: (salt) => createRng(numeric ^ hashSeed(salt)),
  }

  return rng
}

/** Round to the nearest step — stakes land on 50s and 100s, not 47.3179. */
export function quantise(value: number, step: number): number {
  return Math.max(step, Math.round(value / step) * step)
}
